import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Chip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  School as SchoolIcon,
  People as PeopleIcon,
  HowToReg as EnrollmentIcon,
  Hub as HubIcon,
  CheckCircle as CheckIcon,
  Insights as InsightsIcon,
} from '@mui/icons-material';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { adminFetch } from '../utils/adminFetch';

interface DashboardStats {
  totalCourses: number;
  totalStudents: number;
  activeEnrollments: number;
  completionRate: number;
  scormExports: number;
  scormUniqueLearners: number;
  recentEvents: Array<{
    id: string;
    type: 'enrollment' | 'completion' | 'scorm';
    message: string;
    time: string;
  }>;
  courseProgress: Array<{
    courseId: string;
    name: string;
    enrolled: number;
    completed: number;
    averageProgress: number;
    scormUniqueLearners: number;
  }>;
  enrollmentTrend: Array<{
    date: string;
    enrollments: number;
    completions: number;
  }>;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    void fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await adminFetch(`${process.env.REACT_APP_API_URL}/dashboard/stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Impossible de charger les statistiques du tableau de bord');
      }
      const payload = await response.json();
      setStats(payload);
      setLoading(false);
    } catch (error: any) {
      console.error('Impossible de charger les statistiques du tableau de bord:', error);
      setLoading(false);
    }
  };

  const getEventColor = (type: string): 'default' | 'primary' | 'success' => {
    switch (type) {
      case 'enrollment':
        return 'primary';
      case 'completion':
        return 'success';
      default:
        return 'default';
    }
  };

  const topCourses = useMemo(
    () => [...(stats?.courseProgress || [])].sort((a, b) => b.enrolled - a.enrolled).slice(0, 6),
    [stats]
  );

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box mb={4}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Bonjour {user?.firstName}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Vue d'ensemble réelle de l'activité de la plateforme et des événements SCORM récents.
        </Typography>
      </Box>

      <Stack direction="row" spacing={3} mb={4} sx={{ flexWrap: 'wrap', gap: 3 }}>
        <Box sx={{ minWidth: { xs: '100%', sm: '45%', lg: '22%' } }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">Formations</Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats?.totalCourses}
                  </Typography>
                </Box>
                <SchoolIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ minWidth: { xs: '100%', sm: '45%', lg: '22%' } }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">Apprenants</Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats?.totalStudents}
                  </Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ minWidth: { xs: '100%', sm: '45%', lg: '22%' } }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">Inscriptions actives</Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats?.activeEnrollments}
                  </Typography>
                </Box>
                <EnrollmentIcon sx={{ fontSize: 40, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ minWidth: { xs: '100%', sm: '45%', lg: '22%' } }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">Taux de complétion</Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats?.completionRate}%
                  </Typography>
                </Box>
                <CheckIcon sx={{ fontSize: 40, color: 'info.main' }} />
              </Box>
              <Box mt={2}>
                <LinearProgress 
                  variant="determinate" 
                  value={stats?.completionRate || 0} 
                  color="info"
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Stack>

      <Stack direction="row" spacing={3} mb={4} sx={{ flexWrap: 'wrap', gap: 3 }}>
        <Box sx={{ minWidth: { xs: '100%', sm: '48%', lg: '24%' } }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">Exports SCORM</Typography>
                  <Typography variant="h5" fontWeight="bold">{stats?.scormExports || 0}</Typography>
                </Box>
                <HubIcon sx={{ fontSize: 34, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ minWidth: { xs: '100%', sm: '48%', lg: '24%' } }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">Apprenants uniques SCORM</Typography>
                  <Typography variant="h5" fontWeight="bold">{stats?.scormUniqueLearners || 0}</Typography>
                </Box>
                <InsightsIcon sx={{ fontSize: 34, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Stack>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Activité 7 derniers jours</Typography>
              <Box height={280}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stats?.enrollmentTrend || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="enrollments" stroke="#2eab80" strokeWidth={3} />
                    <Line type="monotone" dataKey="completions" stroke="#1976d2" strokeWidth={3} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Événements récents</Typography>
              <Stack spacing={1}>
                {(stats?.recentEvents || []).map((event) => (
                  <Box key={event.id} display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                    <Box>
                      <Typography variant="body2">{event.message}</Typography>
                      <Typography variant="caption" color="text.secondary">{event.time}</Typography>
                    </Box>
                    <Chip size="small" color={getEventColor(event.type)} label={event.type} />
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Stack>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Top formations</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Formation</TableCell>
                  <TableCell>Inscrits</TableCell>
                  <TableCell>Terminés</TableCell>
                  <TableCell>Progression moyenne</TableCell>
                  <TableCell>SCORM uniques</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topCourses.map((course) => (
                  <TableRow key={course.courseId}>
                    <TableCell>{course.name}</TableCell>
                    <TableCell>{course.enrolled}</TableCell>
                    <TableCell>{course.completed}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box sx={{ minWidth: 120 }}>
                          <LinearProgress variant="determinate" value={course.averageProgress} />
                        </Box>
                        <Typography variant="caption">{course.averageProgress}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{course.scormUniqueLearners}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Dashboard; 